import { useEffect, useState } from "react";

type SaleStatus = "COMPLETED" | "CANCELLED";
type TransferStatus = "PENDING" | "SENT" | "CONFIRMED" | "ERROR";

interface SaleListItem {
  id: string;
  localReference: string;
  localCompletedAt: string;
  totalCents: number;
  status: SaleStatus;
  paymentMethod: string;
  eventName: string;
  businessDate: string;
  userName: string;
  deviceName: string;
  units: number;
  lineCount: number;
  transferStatus: TransferStatus | null;
  cancellationTransferStatus: TransferStatus | null;
}

interface SalesResponse {
  items: SaleListItem[];
  total: number;
  summary: {
    completed: number;
    cancelled: number;
    completedCents: number;
    pendingTransfers: number;
  };
}

interface FairOption {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: "DRAFT" | "OPEN" | "CLOSED";
}

interface OperationsOverview {
  events: FairOption[];
}

interface SaleDetail {
  sale: {
    id: string;
    clientSaleId: string;
    localReference: string;
    localCompletedAt: string;
    totalCents: number;
    status: SaleStatus;
    paymentMethod: string;
    eventName: string;
    eventStatus: "DRAFT" | "OPEN" | "CLOSED";
    businessDate: string;
    userName: string;
    deviceName: string;
  };
  items: Array<{
    id: string;
    sourceKeySnapshot: string;
    barcodeSnapshot: string | null;
    nameSnapshot: string;
    unitPriceCents: number;
    quantity: number;
    subtotalCents: number;
  }>;
  transfers: Array<{
    id: string;
    operation: "SALE" | "CANCELLATION";
    status: TransferStatus;
    externalReference: string | null;
    lastError: string | null;
  }>;
  cancellation: {
    reason: string;
    cancelledAt: string;
    cancelledBy: string;
  } | null;
}

const money = new Intl.NumberFormat("ca-ES", {
  style: "currency",
  currency: "EUR",
});
const dateTime = new Intl.DateTimeFormat("ca-ES", {
  dateStyle: "short",
  timeStyle: "short",
});

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  const data = (await response.json().catch(() => null)) as
    | T
    | { error?: string; message?: string }
    | null;
  if (!response.ok) {
    const error = data as { error?: string; message?: string } | null;
    throw new Error(error?.message ?? error?.error ?? "Error de comunicació.");
  }
  return data as T;
}

function paymentLabel(method: string) {
  if (method === "CASH") return "Efectiu";
  if (method === "CARD") return "Targeta";
  return method;
}

function transferLabel(status: TransferStatus | null) {
  if (status === "CONFIRMED") return "Confirmada";
  if (status === "SENT") return "Enviada";
  if (status === "ERROR") return "Error";
  return "Pendent";
}

function transferClass(status: TransferStatus | null) {
  return `transfer-pill transfer-${(status ?? "PENDING").toLowerCase()}`;
}

interface SalesPanelProps {
  eventId?: string;
  title?: string;
  readOnly?: boolean;
}

export function SalesPanel({ eventId, title, readOnly = false }: SalesPanelProps = {}) {
  const [data, setData] = useState<SalesResponse | null>(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [transferStatus, setTransferStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<SaleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [availableEvents, setAvailableEvents] = useState<FairOption[]>([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const effectiveEventId = eventId ?? selectedEventId;
  const selectedEvent = availableEvents.find(
    (event) => event.id === effectiveEventId,
  );

  async function load() {
    if (!effectiveEventId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ limit: "200", offset: String(offset) });
      if (query.trim()) params.set("q", query.trim());
      if (status) params.set("status", status);
      if (transferStatus) params.set("transferStatus", transferStatus);
      params.set("eventId", effectiveEventId);
      setData(await request<SalesResponse>(`/api/v1/sales?${params}`));
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "No s'han pogut carregar les vendes.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (eventId) return;
    let cancelled = false;
    void request<OperationsOverview>("/api/v1/operations/overview")
      .then((overview) => {
        if (cancelled) return;
        setAvailableEvents(overview.events);
        setSelectedEventId((current) => {
          if (overview.events.some((event) => event.id === current)) {
            return current;
          }
          return (
            overview.events.find((event) => event.status === "OPEN")?.id ??
            overview.events[0]?.id ??
            ""
          );
        });
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(
          loadError instanceof Error
            ? loadError.message
            : "No s'han pogut carregar les fires.",
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  async function openDetail(id: string) {
    setBusy(`detail-${id}`);
    setError("");
    try {
      setSelected(await request<SaleDetail>(`/api/v1/sales/${id}`));
    } catch (detailError) {
      setError(
        detailError instanceof Error
          ? detailError.message
          : "No s'ha pogut obrir la venda.",
      );
    } finally {
      setBusy("");
    }
  }

  async function cancelSale() {
    if (!selected || selected.sale.status !== "COMPLETED") return;
    const reason = window.prompt(
      "Motiu de l'anul·lació (quedarà registrat):",
      "",
    );
    if (!reason?.trim()) return;
    if (
      !window.confirm(
        `Anul·lar la venda ${selected.sale.localReference}? Quedarà pendent d'enviar a Àgora.`,
      )
    ) {
      return;
    }
    setBusy("cancel");
    setError("");
    setNotice("");
    try {
      await request(`/api/v1/sales/${selected.sale.id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
      await load();
      await openDetail(selected.sale.id);
      setNotice("Venda anul·lada i preparada per a Àgora.");
    } catch (cancelError) {
      setError(
        cancelError instanceof Error
          ? cancelError.message
          : "No s'ha pogut anul·lar la venda.",
      );
    } finally {
      setBusy("");
    }
  }

  useEffect(() => {
    const timeout = window.setTimeout(() => void load(), 250);
    return () => window.clearTimeout(timeout);
  }, [query, status, transferStatus, effectiveEventId, offset]);

  return (
    <div className="sales-stack">
      {!eventId && (
        <section className="panel sales-event-scope">
          <div>
            <p className="eyebrow">FIRA CONSULTADA</p>
            <h2>{selectedEvent?.name ?? "Selecciona una fira"}</h2>
            <p>Cada consulta mostra exclusivament els tiquets de la fira seleccionada.</p>
          </div>
          <label>
            Fira
            <select
              value={selectedEventId}
              onChange={(event) => {
                setSelectedEventId(event.target.value);
                setOffset(0);
                setSelected(null);
              }}
              disabled={availableEvents.length === 0}
            >
              {availableEvents.length === 0 && (
                <option value="">No hi ha fires disponibles</option>
              )}
              {availableEvents.map((event) => (
                <option value={event.id} key={event.id}>
                  {event.status === "OPEN" ? "Oberta" : event.status === "CLOSED" ? "Tancada" : "Esborrany"}
                  {" · "}{event.name}{" · "}{event.startDate}–{event.endDate}
                </option>
              ))}
            </select>
          </label>
        </section>
      )}

      <section className="sales-summary">
        <article>
          <span>Vendes vàlides</span>
          <strong>{data?.summary.completed ?? 0}</strong>
        </article>
        <article>
          <span>Total registrat</span>
          <strong>{money.format((data?.summary.completedCents ?? 0) / 100)}</strong>
        </article>
        <article>
          <span>Pendents d'Àgora</span>
          <strong>{data?.summary.pendingTransfers ?? 0}</strong>
        </article>
        <article>
          <span>Anul·lades</span>
          <strong>{data?.summary.cancelled ?? 0}</strong>
        </article>
      </section>

      {error && <p className="alert error">{error}</p>}
      {notice && <p className="alert success">{notice}</p>}

      <section className="panel sales-panel">
        <div className="panel-heading sales-heading">
          <div>
            <p className="eyebrow">REGISTRE DE VENDES</p>
            <h2>{title ?? selectedEvent?.name ?? "Operacions de la fira"}</h2>
          </div>
          <span className="badge">{data?.total ?? 0} operacions</span>
        </div>

        <div className="sales-filters">
          <label className="sales-search">
            Cerca
            <input
              type="search"
              placeholder="Referència, producte, ID, codi o dispositiu"
              value={query}
              onChange={(event) => { setQuery(event.target.value); setOffset(0); }}
            />
          </label>
          <label>
            Estat
            <select value={status} onChange={(event) => { setStatus(event.target.value); setOffset(0); }}>
              <option value="">Totes</option>
              <option value="COMPLETED">Completades</option>
              <option value="CANCELLED">Anul·lades</option>
            </select>
          </label>
          <label>
            Transferència
            <select
              value={transferStatus}
              onChange={(event) => { setTransferStatus(event.target.value); setOffset(0); }}
            >
              <option value="">Totes</option>
              <option value="PENDING">Pendents</option>
              <option value="SENT">Enviades</option>
              <option value="CONFIRMED">Confirmades</option>
              <option value="ERROR">Amb error</option>
            </select>
          </label>
          <button className="secondary" type="button" disabled={loading} onClick={() => void load()}>
            {loading ? "Carregant…" : "Actualitzar"}
          </button>
        </div>

        <div className="sales-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Venda</th>
                <th>Data</th>
                <th>Unitats</th>
                <th>Pagament</th>
                <th>Dispositiu</th>
                <th>Àgora</th>
                <th>Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data?.items.map((sale) => (
                <tr className={sale.status === "CANCELLED" ? "sale-cancelled" : ""} key={sale.id}>
                  <td>
                    <strong className="sale-reference">{sale.localReference}</strong>
                    <small>{sale.eventName} · {sale.businessDate}</small>
                    {sale.status === "CANCELLED" && (
                      <span className="status-pill status-cancelled">Anul·lada</span>
                    )}
                  </td>
                  <td>{dateTime.format(new Date(sale.localCompletedAt))}</td>
                  <td>{sale.units} <small>({sale.lineCount} línies)</small></td>
                  <td>{paymentLabel(sale.paymentMethod)}</td>
                  <td>{sale.deviceName}<small>{sale.userName}</small></td>
                  <td>
                    <span className={transferClass(sale.transferStatus)}>
                      {transferLabel(sale.transferStatus)}
                    </span>
                    {sale.status === "CANCELLED" && (
                      <small>Anul·lació: {transferLabel(sale.cancellationTransferStatus)}</small>
                    )}
                  </td>
                  <td className="sales-amount">{money.format(sale.totalCents / 100)}</td>
                  <td>
                    <button className="secondary compact-button" type="button" disabled={Boolean(busy)} onClick={() => void openDetail(sale.id)}>
                      {busy === `detail-${sale.id}` ? "…" : "Veure"}
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && data?.items.length === 0 && (
                <tr><td className="catalog-empty" colSpan={8}>No hi ha vendes amb aquests filtres.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {(offset > 0 || (data?.total ?? 0) > offset + 200) && (
          <div className="sales-pagination">
            <button className="secondary" type="button" disabled={loading || offset === 0} onClick={() => setOffset((current) => Math.max(0, current - 200))}>← Anteriors</button>
            <span>{offset + 1}–{Math.min(offset + 200, data?.total ?? 0)} de {data?.total ?? 0}</span>
            <button className="secondary" type="button" disabled={loading || offset + 200 >= (data?.total ?? 0)} onClick={() => setOffset((current) => current + 200)}>Següents →</button>
          </div>
        )}
      </section>

      {selected && (
        <div className="sale-detail-overlay" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setSelected(null);
        }}>
          <section className="sale-detail" role="dialog" aria-modal="true" aria-labelledby="sale-detail-title">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">DETALL DE VENDA</p>
                <h2 id="sale-detail-title">{selected.sale.localReference}</h2>
              </div>
              <button className="secondary compact-button" type="button" onClick={() => setSelected(null)}>Tancar</button>
            </div>

            <div className="sale-detail-meta">
              <div><span>Fira i jornada</span><strong>{selected.sale.eventName} · {selected.sale.businessDate}</strong></div>
              <div><span>Data de venda</span><strong>{dateTime.format(new Date(selected.sale.localCompletedAt))}</strong></div>
              <div><span>Dispositiu</span><strong>{selected.sale.deviceName}</strong></div>
              <div><span>Operador</span><strong>{selected.sale.userName}</strong></div>
            </div>

            <div className="sale-lines">
              <div className="sale-lines-heading"><strong>Producte</strong><strong>Quantitat</strong><strong>Import</strong></div>
              {selected.items.map((item) => (
                <div key={item.id}>
                  <span>
                    <strong>{item.nameSnapshot}</strong>
                    <small>ID Àgora {item.sourceKeySnapshot}{item.barcodeSnapshot ? ` · ${item.barcodeSnapshot}` : ""}</small>
                  </span>
                  <b>{item.quantity}</b>
                  <b>{money.format(item.subtotalCents / 100)}</b>
                </div>
              ))}
            </div>

            <div className="sale-detail-total">
              <span>{paymentLabel(selected.sale.paymentMethod)}</span>
              <strong>{money.format(selected.sale.totalCents / 100)}</strong>
            </div>

            <div className="transfer-timeline">
              {selected.transfers.map((transfer) => (
                <div key={transfer.id}>
                  <span className={transferClass(transfer.status)}>
                    {transfer.operation === "SALE" ? "Venda" : "Anul·lació"} · {transferLabel(transfer.status)}
                  </span>
                  <small>{transfer.externalReference ? `Ref. Àgora ${transfer.externalReference}` : "Esperant connector d'Àgora"}</small>
                  {transfer.lastError && <em>{transfer.lastError}</em>}
                </div>
              ))}
            </div>

            {selected.cancellation && (
              <p className="cancellation-note">
                <strong>Venda anul·lada:</strong> {selected.cancellation.reason} · {dateTime.format(new Date(selected.cancellation.cancelledAt))} per {selected.cancellation.cancelledBy}
              </p>
            )}

            {selected.sale.status === "COMPLETED" && !readOnly && selected.sale.eventStatus === "OPEN" && (
              <button className="danger-button" type="button" disabled={Boolean(busy)} onClick={() => void cancelSale()}>
                {busy === "cancel" ? "Anul·lant…" : "Anul·lar venda"}
              </button>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
